import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Scaffold } from 'layouts';
import { notify } from 'services';
import { useUploadBlockedProductFiles } from 'hooks/api/blocked-products';
import {
  useFetchScheduledInventoryDataImports,
} from 'hooks/api/scheduled-inventory-data-imports';
import {
  useScheduledInventoryDataImportsSearchParams,
} from 'hooks/store/scheduled-inventory-data-imports';
import { DataTable } from 'components/ui/DataTable';

import { useScheduledDataImportColumns } from './hooks';
import { UploadDataForm, type UploadDataPayload } from './components';
import { TabbedNavigation } from '../shared-components';

const NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_SUCCESS = false;

export const ImportDataPage = () => {
  const navigate = useNavigate();
  const {
    uploadProductsFile,
    uploadProductPricesFile,
    uploadProductReasonsFile,
    uploadPlannerAndMaterialFile,
    uploadDestinationPlantMaterialsFile,
  } = useUploadBlockedProductFiles();
  const { filters } = useScheduledInventoryDataImportsSearchParams();
  const { fetchScheduledImports } = useFetchScheduledInventoryDataImports();
  const columns = useScheduledDataImportColumns();

  const queryClient = useQueryClient();

  const scheduledImportsQuery = useQuery({
    queryKey: ['fetch-scheduled-imports', filters],
    queryFn: () => fetchScheduledImports({ filters }),
  });

  const refetchScheduledImports = async () => {
    await queryClient.invalidateQueries(['fetch-scheduled-imports']);
  };

  const uploadProductsMutation = useMutation({
    mutationFn: uploadProductsFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      notify('success', 'Importação de produtos iniciada.');
      if (NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_SUCCESS) {
        navigate('/inspecao-estoque/produtos-bloqueados');
      }
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const uploadProductPricesMutation = useMutation({
    mutationFn: uploadProductPricesFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      notify('success', 'Importação de custos iniciada.');
      if (NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_SUCCESS) {
        navigate('/inspecao-estoque/produtos-bloqueados');
      }
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const uploadProductReasonsMutation = useMutation({
    mutationFn: uploadProductReasonsFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      notify('success', 'Importação de motivos de bloqueio iniciada.');
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const uploadPlannerAndMaterialMutation = useMutation({
    mutationFn: uploadPlannerAndMaterialFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      notify(
        'success',
        'Importação de associação de planejadores por produto iniciada.',
      );
    },
    onError(e: any) {
      notify('error', e?.message);
    },
  });

  const uploadDestinationPlantMaterialMutation = useMutation({
    mutationFn: uploadDestinationPlantMaterialsFile,
    onSuccess: async () => {
      await refetchScheduledImports();
      notify(
        'success',
        'Importação de associação de materiais por planta de destino iniciada.',
      );
    },
    onError(e: any) {
      notify('error', e?.message);
    },
  });

  const onUploadDataFormSubmit = React.useCallback(
    (formData: UploadDataPayload) => {
      const uploadType = formData.uploadType;
      if (uploadType === 'blocked-products') {
        uploadProductsMutation.mutate(formData);
      } else if (uploadType === 'blocked-product-prices') {
        uploadProductPricesMutation.mutate(formData);
      } else if (uploadType === 'blocked-product-reasons') {
        uploadProductReasonsMutation.mutate(formData);
      } else if (uploadType === 'planner-and-material-association') {
        uploadPlannerAndMaterialMutation.mutate(formData);
      } else if (uploadType === 'destination-plant-material-association') {
        uploadDestinationPlantMaterialMutation.mutate(formData);
      }
    }, []);

  return (
    <Scaffold>
      <TabbedNavigation />
      <UploadDataForm
        onSubmit={onUploadDataFormSubmit}
        loading={
          uploadProductsMutation.isLoading ||
          uploadProductPricesMutation.isLoading ||
          uploadProductReasonsMutation.isLoading ||
          uploadPlannerAndMaterialMutation.isLoading
        }
      />
      <h2 className={'font-GilroySemibold text-gray-600 mt-4'}>
        Fila de processamento de importações
      </h2>
      <DataTable
        data={scheduledImportsQuery?.data ?? []}
        isLoading={scheduledImportsQuery.isLoading}
        columns={columns}
        showControlColumns={false}
      />
    </Scaffold>
  );
};